import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ProductCatalog,
  CartPanel,
  PaymentSummary,
  ProductDialog,
  ResumeIntakeBanner,
  useShopBlockLogic,
  type ShopStep,
} from "@tellescope/shop";
import { usePortalTheming } from "../../../shared/store/portalSchemaStore";
import { useEnduserStore } from "../../store/hooks";
import { useShopProductsFilter } from "../../hooks/useShopProductsFilter";
import type { ShopBlock as ShopBlockType } from "../../../shared/schema";

export interface ShopBlockProps {
  block: ShopBlockType;
}

/** Query param the global cart icon (PortalContent) sets to deep-link into this block's cart step. */
export const SHOP_STEP_QUERY_PARAM = "shopStep";
const STEP_QUERY_PARAM = SHOP_STEP_QUERY_PARAM;

// Only catalog and cart can be entered from a link. The payment and confirmation steps read a cart
// snapshot taken when the enduser presses Checkout, so landing on them directly would show an empty,
// $0 order; those links open the cart instead. "checkout-questions" has no render case in v1 (no
// form-gated products), so it falls back to catalog rather than a blank content area. Revisit it
// once the forms fast follow restores that step.
const getDeepLinkStep = (value: string | null): ShopStep | undefined => {
  if (value === "catalog" || value === "cart") return value;
  if (value === "checkout-payment" || value === "confirmation") return "cart";
  return undefined;
};

export const ShopBlock = ({ block }: ShopBlockProps) => {
  const { t } = useTranslation("shop");
  const { products: allProducts, title, subtitle, emptyMessage, confirmationMessage, stripeKey } = block.props;
  const { filteredProducts: products, isLoading: isLoadingProducts } = useShopProductsFilter(allProducts);
  const theming = usePortalTheming();
  const session = useEnduserStore((state) => state.session);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStepParam = searchParams.get(STEP_QUERY_PARAM);
  // React Router assigns a fresh key to every pushed navigation entry, even one whose URL string
  // is identical to the current one (e.g. clicking a plain nav link back to this same shop page
  // while sitting on an internal step, like checkout-payment, that was reached without ever
  // touching shopStep). Without this, re-navigating to that identical param-less URL wouldn't
  // change initialStepParam at all, so the sync effect below would never re-fire and the enduser
  // would stay stuck on whatever internal step they were on.
  const locationKey = useLocation().key;

  // Deep-link support for the global cart icon: a shopStep query param sets the block's initial
  // step, so navigating from elsewhere in the portal can land on the cart.
  const logic = useShopBlockLogic({
    products,
    initialStep: getDeepLinkStep(initialStepParam),
  });

  // The block only mounts once per page visit — if the enduser is already viewing this shop
  // block and navigates again (e.g. clicking the cart icon, or "Shop" in the nav bar), the query
  // param changes but the component doesn't remount, so the initial-step state above never
  // re-applies. Sync explicitly on every REAL param change, defaulting back to the catalog when
  // the param is absent (e.g. a plain "/shop" link) so navigating back to the shop's own nav item
  // doesn't leave the enduser stranded on whatever step they were previously on.
  //
  // "Real" excludes the specific case where the effect below clears a now-stale param after an
  // in-block action already moved logic.step somewhere else (e.g. "Checkout" moving from "cart" to
  // "checkout-questions", or "Browse medications" moving from "cart" to "catalog"). That clearing
  // causes initialStepParam to go from "cart" to null on the very next render — without this guard,
  // this effect would treat that exactly like an external "navigate to a plain /shop link" and force
  // the step back to "catalog", clobbering whatever step the in-block action had just set.
  // ownParamClearRef marks exactly that one render so it can be skipped here.
  // setStep is useState's setter, so its identity is stable across renders.
  const ownParamClearRef = useRef(false);
  useEffect(() => {
    if (ownParamClearRef.current) {
      ownParamClearRef.current = false;
      return;
    }
    logic.setStep(getDeepLinkStep(initialStepParam) ?? "catalog");
  }, [initialStepParam, locationKey, logic.setStep]);

  // Once the enduser navigates away from the cart step via an in-block action (e.g. "Browse
  // medications" from an emptied cart, or "Checkout" moving on to the intake wizard), clear the
  // now-stale shopStep=cart param from the URL. Without this, the URL and internal step silently
  // diverge — and since the global cart icon always navigates to the same pageId + {shopStep:
  // "cart"}, a later click would be a no-op as far as react-router is concerned (identical URL, no
  // location change), leaving the cart icon unresponsive until the URL changed some other way.
  //
  // wasOnCartRef tracks whether logic.step was "cart" as of the last time this effect ran — reset
  // on EVERY transition away from "cart" (not only ones caught by the clearing branch below),
  // since there's more than one way to leave the cart step without the URL param changing (e.g. an
  // in-block action, or navigating to a plain param-less shop link). If this were only reset
  // inside the clearing branch, a later transition via one of those other paths would leave it
  // stuck true, and the next cart-icon click would see initialStepParam flip to "cart" while
  // logic.step still reads its pre-sync stale value and immediately clear the param again before
  // the sync effect's setStep("cart") render above has a chance to land.
  const wasOnCartRef = useRef(false);
  useEffect(() => {
    const wasOnCart = wasOnCartRef.current;
    wasOnCartRef.current = logic.step === "cart";

    if (wasOnCart && logic.step !== "cart" && getDeepLinkStep(initialStepParam) === "cart") {
      ownParamClearRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(STEP_QUERY_PARAM);
          return next;
        },
        { replace: true }
      );
    }
  }, [logic.step, initialStepParam, setSearchParams]);

  return (
    <div className="flex flex-col">
      {logic.showResumeBanner && (
        <ResumeIntakeBanner
          className="sticky top-0 z-20 flex-shrink-0"
          medName={logic.currentMedName}
          stepCurrent={logic.currentIntakeStep}
          stepTotal={logic.totalIntakeSteps}
          complete={logic.checkoutProgress.intakeComplete && logic.canResumeToPayment}
          onResume={logic.resumeIntake}
          onDismiss={logic.dismissResumeBanner}
        />
      )}

      {logic.step === "catalog" && (
        <ProductCatalog
          products={products}
          title={title}
          subtitle={subtitle}
          emptyMessage={emptyMessage}
          onLearnMore={logic.setLearnMoreProduct}
          primaryColorHex={theming?.primaryColor}
        />
      )}

      {logic.step === "cart" && (
        <CartPanel
          products={products}
          isLoadingProducts={isLoadingProducts}
          onCheckout={logic.enterCheckout}
          onShop={logic.goToShop}
        />
      )}

      {/*
        v1 ships with no form-gated products, so logic.step never actually becomes
        "checkout-questions" (see useShopBlockLogic.ts's enterCheckout) and getDeepLinkStep above rejects
        it as a deep-link value — there is deliberately no render case for it here. The forms fast
        follow restores one (see CheckoutWizard.tsx, kept in place for this purpose).
      */}

      {(logic.step === "checkout-payment" || logic.step === "confirmation") && (
        <PaymentSummary
          cartRows={logic.paymentCartRows}
          onBack={logic.cartMedications.length > 0 ? () => logic.setStep("checkout-questions") : logic.goToCart}
          backLabel={logic.cartMedications.length > 0 ? t("Back to questions") : t("Back to cart")}
          onConfirm={logic.goToConfirmation}
          onBackToShop={logic.goToShop}
          confirmationMessage={confirmationMessage}
          stripeKey={stripeKey}
          session={session}
        />
      )}

      <ProductDialog product={logic.learnMoreProduct} onClose={() => logic.setLearnMoreProduct(null)} />
    </div>
  );
};
